import clsx from 'clsx'
import xss from 'xss'

import { Button } from '@wartek-id/button-v1'
import { Text } from '@wartek-id/text'

import { isLaterThan } from 'utils/datetime'
import { useSETrackEvent } from '../hooks/useSETrackEvent'
import { useCompareUjikomPeriod } from '../utils'
import BlockTimeRemaining from './BlockTimeRemaining'
import { useMediaQueryHelper } from '../hooks/useMediaQueryHelper'
import DownloadDecreeBlock from './DownloadDecreeBlock'

import type { FC } from 'react'
import type {
  ICompareUjikomPeriodResponse,
  IUjikomData,
  IUjikomPeriodData,
  IUjikomResultData,
  TUjikomState,
  TUjikomStateOption,
} from 'types/content-platform'

import AccountMenuIcon from 'assets/icons/account-menu-icon.svg'
import DoneUjikomIcon from 'assets/icons/done-ujikom-icon.svg'

interface UjikomDescriptionProps {
  period: ICompareUjikomPeriodResponse
  result: IUjikomResultData
  stateName: TUjikomStateOption
  state: TUjikomState
}

const UjikomDescription: FC<UjikomDescriptionProps> = ({
  period,
  result,
  stateName,
  state,
}): React.ReactElement => {
  if (
    (period.isAfterExamPeriod && stateName === 'FORM_NOT_STARTED') ||
    stateName === 'FORM_CLOSED'
  ) {
    // To only be displayed if today is after the closing period (eg. 12 May) and the user hasn't submit anything
    // or when form closed
    return (
      <div
        className={clsx('bg-surface-warning-subdued p-3 rounded-lg', {
          'mb-4': state?.link && state?.cta, // if has CTA button
        })}
      >
        <Text variant="body">
          Maaf, pelaksanaan Uji Kompetensi sudah selesai.
          <br />
          <br />
          Anda bisa mendapatkan sertifikat kelulusan jika sudah mengikuti ujian.
        </Text>
      </div>
    )
  }

  if (period.isInExamPeriod && stateName === 'FORM_STARTED') {
    // To prevent from the page keep reloading endlessly, we will
    // make sure that the `endTime` passed as prop is later than the current time
    const endTime = new Date(result?.endTime)
    if (isLaterThan(endTime, new Date())) {
      return (
        <BlockTimeRemaining
          className={clsx(state.link && state.cta && 'mb-4')}
          endTime={endTime}
          ticks
          callback={() => {
            setTimeout(() => {
              location?.reload()
            }, 1000) // Reload once the time ends with delay 1s
          }}
        />
      )
    }
  }

  return (
    <>
      {state?.description && (
        <div
          className="bg-cyan-5 rounded-lg p-3 mb-4 ujikom-wysiwyg"
          dangerouslySetInnerHTML={{ __html: xss(state.description) }}
        />
      )}
    </>
  )
}

interface IUjikomCardProps {
  UjikomData: IUjikomData
  UjikomResult: IUjikomResultData
  UjikomState: string
  UjikomPeriod: IUjikomPeriodData
  currentDateTimeServer: string
  handleOpenMore: () => void
}

const UjikomCard = ({
  UjikomData,
  UjikomResult,
  UjikomState,
  UjikomPeriod,
  currentDateTimeServer,
  handleOpenMore,
}: IUjikomCardProps) => {
  const { trackButtonClick } = useSETrackEvent()
  const { isDesktop } = useMediaQueryHelper()
  let IsPeriod = useCompareUjikomPeriod(currentDateTimeServer, UjikomPeriod)
  const state = UjikomData.states.find((state) => state.state === UjikomState)

  return (
    <div className="rounded-lg bg-white my-5 mx-4 px-3 py-5">
      <div className="flex flex-row gap-2 mb-3">
        {state?.finished ? (
          <DoneUjikomIcon width={24} height={24} />
        ) : (
          <AccountMenuIcon width={24} height={24} />
        )}
        <Text variant="heading-sm">{UjikomData.title}</Text>
      </div>
      {state?.headline && (
        <div
          className="mb-4"
          dangerouslySetInnerHTML={{ __html: xss(state.headline) }}
        />
      )}

      {(state?.state === 'FORM_CERTIFICATE_GENERATED' ||
        state?.state === 'FORM_COMPLETED') && (
        <div className="mb-4">
          <Text as="span">Selamat, Anda lulus!</Text>
          <Text
            as="span"
            color="primary"
            className="ml-1 cursor-pointer"
            onClick={handleOpenMore}
          >
            Selengkapnya
          </Text>
          <Text as="span">.</Text>
        </div>
      )}

      {state?.state === 'FORM_CONDITIONAL_QUALIFIED' && (
        <div className="mb-4">
          <Text as="span">
            Perlu klarifikasi data melalui SIMPKB paling lambat 1 Juli 2023.{' '}
          </Text>
          <Text
            as="span"
            color="primary"
            className="cursor-pointer"
            onClick={handleOpenMore}
          >
            Selengkapnya
          </Text>
          <Text as="span">.</Text>
        </div>
      )}

      {state?.state === 'FORM_NOT_QUALIFIED' && (
        <div className="mb-4">
          <Text as="span">
            Maaf, Anda belum bisa diangkat ke JF Guru Ahli Muda.{' '}
          </Text>
          <Text
            as="span"
            color="primary"
            className="cursor-pointer"
            onClick={handleOpenMore}
          >
            Selengkapnya
          </Text>
          <Text as="span">.</Text>
        </div>
      )}

      <UjikomDescription
        period={IsPeriod}
        result={UjikomResult}
        stateName={UjikomState as TUjikomStateOption} // Expected to be mapped to TUjikomStateOption
        state={state}
      />

      <div>
        {state?.state === 'FORM_NOT_QUALIFIED' && (
          <DownloadDecreeBlock className="mt-4" />
        )}
        {state?.link && state?.cta && (
          <Button
            color="black"
            disabled={!state.finished && !IsPeriod.isInExamPeriod}
            size="lg"
            fullWidth={!isDesktop}
            onClick={() => {
              // Track clicks only for Ikuti Uji Kompetensi
              if (['FORM_NOT_STARTED'].includes(state.state)) {
                trackButtonClick({
                  pemKomPeriod: UjikomPeriod,
                  activityType: 'CLICK_PEMKOM_BUTTON',
                })
              }

              // Track clicks only for Lanjutkan Uji Kompetensi
              else if (['FORM_STARTED'].includes(state.state)) {
                trackButtonClick({
                  pemKomPeriod: UjikomPeriod,
                  activityType: 'CLICK_PEMKOM_RESUME_BUTTON',
                })
              }

              // Track clicks only for Cek sertifikat
              else if (['FORM_CERTIFICATE_GENERATED'].includes(state.state)) {
                trackButtonClick({
                  pemKomPeriod: UjikomPeriod,
                  activityType: 'CLICK_CHECK_CERTIFICATE',
                })
              }

              window.location.href = state.link
            }}
          >
            {state.cta}
          </Button>
        )}
      </div>
    </div>
  )
}

export default UjikomCard
